import { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks/hooks";
import {
  queryChanged,
  searchCleared,
  searchFailed,
  searchStarted,
  searchSucceeded,
  nextPageStarted,
  nextPageSucceeded,
  nextPageFailed,
  resultSelected,
  scopeRootResolved,
  selectionCleared,
  selectOutlineSearch,
  selectOutlineFilter,
  hasActiveSearchCriteria,
  MIN_SEARCH_TERM_LENGTH,
  filterChanged,
  OutlineSearchState,
} from "../../../redux/feature/cpm/outline-search-slice";
import { searchDocumentParts } from "../../../api/document/DocumentsPart";
import { DocumentPermissionTO } from "../../../api/models/document-permission-to";
import { OutlineFilterStatus } from "../workflowStatusCategory";

const DEBOUNCE_MS = 2000;
const PAGE_SIZE = 20;
const ANTI_FLASH_MS = 200;

let requestCounter = 0;

function nextRequestId(): string {
  requestCounter += 1;
  return `req-${requestCounter}`;
}

export interface UseOutlineSearchResult {
  query: string;
  results: DocumentPermissionTO[];
  status: OutlineSearchState["status"];
  nextPageStatus: OutlineSearchState["nextPageStatus"];
  hasMore: boolean;
  error: string | null;
  scopeRootId: string | null;
  selectedDocPartId: string | null;
  setQuery: (q: string) => void;
  setFilter: (next: { assignedToMe: boolean; statusFilter: OutlineFilterStatus | null }) => void;
  fetchNextPage: () => void;
  selectResult: (row: DocumentPermissionTO) => void;
  clear: () => void;
  refireQuery: () => void;
  refireFilterOnly: () => void;
  flushSearch: () => void;
}

/**
 * Manages outline search state: debounce, AbortController, anti-flash (≥200ms skeleton),
 * pagination, scope-root changes, and Redux dispatch.
 * Takes scopeRootId as a parameter – derived from the tree's current root.
 */
export function useOutlineSearch(scopeRootId: string | null): UseOutlineSearchResult {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectOutlineSearch);
  const { assignedToMe, statusFilter } = useAppSelector(selectOutlineFilter);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortController = useRef<AbortController | null>(null);
  // Track when the fetch started for anti-flash
  const fetchStartTime = useRef<number | null>(null);

  // Resolve scope root changes
  const prevScopeRootId = useRef<string | null>(scopeRootId);

  const fireFetch = useCallback(
    (
      q: string,
      rootId: string,
      page: number,
      reqId: string,
      filter: { assignedToMe: boolean; statusFilter: OutlineFilterStatus | null }
    ) => {
      if (abortController.current) {
        abortController.current.abort();
      }
      abortController.current = new AbortController();
      fetchStartTime.current = Date.now();

      if (page === 0) {
        dispatch(searchStarted({ reqId, q }));
      } else {
        dispatch(nextPageStarted({ reqId }));
      }

      searchDocumentParts(rootId, q, page, PAGE_SIZE, abortController.current.signal, {
        assignedToMe: filter.assignedToMe,
        status: filter.statusFilter,
      })
        .then((resp) => {
          const elapsed = Date.now() - (fetchStartTime.current ?? 0);
          const remaining = Math.max(0, ANTI_FLASH_MS - elapsed);
          const pageResult = {
            content: resp._embedded?.content ?? [],
            hasMore: resp.page ? resp.page.number + 1 < resp.page.totalPages : false,
          };
          const applyResult = () => {
            if (page === 0) {
              dispatch(searchSucceeded({ reqId, p: pageResult }));
            } else {
              dispatch(
                nextPageSucceeded({
                  reqId,
                  p: pageResult,
                  page,
                  effectiveQuery: q,
                })
              );
            }
          };
          if (remaining > 0) {
            setTimeout(applyResult, remaining);
          } else {
            applyResult();
          }
        })
        .catch((err: unknown) => {
          const isAbort =
            err instanceof Error &&
            (err.name === "AbortError" || err.name === "CanceledError" || err.message === "canceled");
          if (!isAbort) {
            const message = err instanceof Error ? err.message : String(err);
            if (page === 0) {
              dispatch(searchFailed({ reqId, err: message }));
            } else {
              dispatch(nextPageFailed({ reqId, err: message }));
            }
          }
        });
    },
    [dispatch]
  );

  // Scope root change effect
  useEffect(() => {
    // Always publish the current scope root to the slice so selectors
    // (e.g. the footer Edit-button enable check) can compare against it.
    dispatch(scopeRootResolved({ rootId: scopeRootId }));
    const prev = prevScopeRootId.current;
    prevScopeRootId.current = scopeRootId;
    if (scopeRootId !== prev) {
      // Abort any in-flight
      if (abortController.current) {
        abortController.current.abort();
        abortController.current = null;
      }
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      dispatch(selectionCleared());
      // If query is long enough, fire immediately (no debounce per FR-002 AC06).
      // NOTE: scopeRootResolved (dispatched above, in this same effect) unconditionally
      // resets the store's filter to {assignedToMe:false, statusFilter:null} whenever the
      // root changes - but the assignedToMe/statusFilter selector values captured by this
      // render are the OLD root's pre-reset values (stale by the time this callback runs).
      // Using them here would incorrectly resurrect the previous root's filter for the new
      // root. This site intentionally hardcodes the just-reset filter instead of the
      // selector snapshot; this is functionally identical to the previous
      // `q.length >= MIN_QUERY_LENGTH` gate, since a filter can never be legitimately
      // active immediately after a scope-root change.
      const q = state.query.trim();
      if (hasActiveSearchCriteria(q, false, null) && scopeRootId) {
        const reqId = nextRequestId();
        fireFetch(q, scopeRootId, 0, reqId, { assignedToMe: false, statusFilter: null });
      }
    }
    // Intentionally omit dispatch/fireFetch/state from deps: this effect must only
    // re-run when scopeRootId changes, not on every re-render. Refs are used for
    // mutable state (debounceTimer, abortController) to avoid stale closures.
  }, [scopeRootId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      if (abortController.current) abortController.current.abort();
      dispatch(selectionCleared());
    };
    // Intentionally empty deps: this cleanup effect runs only on unmount.
  }, []);

  const setQuery = useCallback(
    (q: string) => {
      dispatch(queryChanged(q));

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }

      const trimmed = q.trim();
      if (!hasActiveSearchCriteria(trimmed, assignedToMe, statusFilter)) {
        if (abortController.current) {
          abortController.current.abort();
          abortController.current = null;
        }
        return;
      }

      if (!scopeRootId) return;

      // IDS-7324: a term shorter than the minimum must never start a text search on its own.
      // Execution only reaches this point with a non-empty short term when a filter is active
      // (the guard above already returned otherwise), so the current filter-only results stay
      // on screen and we simply skip scheduling a fetch until the term is long enough.
      if (trimmed.length > 0 && trimmed.length < MIN_SEARCH_TERM_LENGTH) {
        return;
      }

      // Text stays debounced; only filter changes (setFilter) fire immediately.
      debounceTimer.current = setTimeout(() => {
        debounceTimer.current = null;
        const reqId = nextRequestId();
        fireFetch(trimmed, scopeRootId, 0, reqId, { assignedToMe, statusFilter });
      }, DEBOUNCE_MS);
    },
    [dispatch, fireFetch, scopeRootId, assignedToMe, statusFilter]
  );

  // Applies a new assignedToMe/status filter immediately: no debounce, no min-length
  // gate (unlike setQuery). Cancels any pending debounced text-fetch and any in-flight
  // request first, then either fires exactly one fresh page-0 fetch (if criteria are
  // still active) or clears the search (if criteria became inactive).
  const setFilter = useCallback(
    (next: { assignedToMe: boolean; statusFilter: OutlineFilterStatus | null }) => {
      dispatch(filterChanged(next));

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }

      const q = state.query.trim();
      if (hasActiveSearchCriteria(q, next.assignedToMe, next.statusFilter) && scopeRootId) {
        // Use `next` (not the assignedToMe/statusFilter selector values from this render):
        // the filterChanged dispatch above has not yet propagated to this closure's
        // snapshot, so fireFetch must be given the filter being applied right now.
        const reqId = nextRequestId();
        fireFetch(q, scopeRootId, 0, reqId, next);
      } else {
        // Criteria became inactive (filter cleared and no active text): abort any
        // in-flight request (fireFetch is not called on this branch, so nothing else
        // would abort it) and reset to the empty/idle state.
        if (abortController.current) {
          abortController.current.abort();
          abortController.current = null;
        }
        dispatch(searchCleared());
      }
    },
    [dispatch, state.query, scopeRootId, fireFetch]
  );

  const fetchNextPage = useCallback(() => {
    if (state.status !== "success" || !state.hasMore || state.nextPageStatus === "loading") return;
    const effectiveQuery = state.effectiveQuery;
    // `=== null` (not a falsy/length check): an active filter can legitimately produce
    // an empty-string effectiveQuery, which must still be allowed to paginate.
    if (!scopeRootId || effectiveQuery === null || !state.requestId) return;
    if (!hasActiveSearchCriteria(effectiveQuery, assignedToMe, statusFilter)) return;

    const reqId = state.requestId;
    const nextPage = state.page + 1;
    fireFetch(effectiveQuery, scopeRootId, nextPage, reqId, { assignedToMe, statusFilter });
  }, [state, scopeRootId, fireFetch, assignedToMe, statusFilter]);

  const selectResult = useCallback(
    (row: DocumentPermissionTO) => {
      if (scopeRootId) {
        dispatch(resultSelected({ id: row.documentPart.id, scopeRootId }));
      }
    },
    [dispatch, scopeRootId]
  );

  const clear = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    if (abortController.current) {
      abortController.current.abort();
      abortController.current = null;
    }
    dispatch(searchCleared());
  }, [dispatch]);

  const refireQuery = useCallback(() => {
    const q = state.query.trim();
    if (!hasActiveSearchCriteria(q, assignedToMe, statusFilter) || !scopeRootId) return;
    const reqId = nextRequestId();
    fireFetch(q, scopeRootId, 0, reqId, { assignedToMe, statusFilter });
  }, [state.query, scopeRootId, fireFetch, assignedToMe, statusFilter]);

  // IDS-5755: re-fire a filter-only search (empty text query) with the currently active
  // filter. Used by the search-bar X button, which clears the text query only: when a
  // status / "assigned to me" filter is still active, the filtered results must stay
  // visible instead of falling back to the full tree. The query is hardcoded to "" because
  // searchCleared has just reset the store query but this render's state.query snapshot is
  // stale (same stale-closure reason as the scope-root effect above).
  const refireFilterOnly = useCallback(() => {
    if (!scopeRootId) return;
    if (!hasActiveSearchCriteria("", assignedToMe, statusFilter)) return;
    const reqId = nextRequestId();
    fireFetch("", scopeRootId, 0, reqId, { assignedToMe, statusFilter });
  }, [scopeRootId, fireFetch, assignedToMe, statusFilter]);

  // Cancels any pending debounce and fires the search immediately for the current
  // query. No-op when there is no active search criteria or no scope root is available.
  const flushSearch = useCallback(() => {
    const q = state.query.trim();
    if (!hasActiveSearchCriteria(q, assignedToMe, statusFilter) || !scopeRootId) return;

    // IDS-7324: same rule as setQuery - a term shorter than the minimum must never start a text
    // search on its own, even when a filter is active and would otherwise satisfy
    // hasActiveSearchCriteria.
    if (q.length > 0 && q.length < MIN_SEARCH_TERM_LENGTH) return;

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    const reqId = nextRequestId();
    fireFetch(q, scopeRootId, 0, reqId, { assignedToMe, statusFilter });
  }, [state.query, scopeRootId, fireFetch, assignedToMe, statusFilter]);

  return {
    query: state.query,
    results: state.results,
    status: state.status,
    nextPageStatus: state.nextPageStatus,
    hasMore: state.hasMore,
    error: state.error,
    scopeRootId,
    selectedDocPartId: state.selectedDocPartId,
    setQuery,
    setFilter,
    fetchNextPage,
    selectResult,
    clear,
    refireQuery,
    refireFilterOnly,
    flushSearch,
  };
}
